<script lang="ts">
	import '../app.css';
	import { locale, isAuthAction as authActionPath } from '$lib/localization/locale';
	import { publicIndexPage, localizedPath } from '$lib/localization/routes';
	import { observeAppearance } from '$lib/appearance';
	import { page } from '$app/stores';
	import { onMount, type Snippet } from 'svelte';
	import { logLandingBoot } from '$lib/landingBootLog';
	import { IS_AQUEDUCT } from '$lib/site';
	import { FIREBASE_AUTH_DOMAIN, SITE_ORIGIN } from '$lib/siteConfig';
	import { faviconOverride } from '$lib/favicon';
	import AnalyticsConsentBanner from '$lib/AnalyticsConsentBanner.svelte';
	import { initializeAnalyticsConsent } from '$lib/analyticsConsent';

	let { children }: { children: Snippet } = $props();

	logLandingBoot('root layout: component script evaluated');
	const canonicalUrl: string = $derived(new URL($page.url.pathname, SITE_ORIGIN).href);
	const isAuthAction: boolean = $derived(authActionPath($page.url.pathname));

	$effect(() => {
		document.documentElement.lang = $locale;
	});
	onMount(observeAppearance);
	onMount(() => {
		logLandingBoot('root layout: mounted', {
			documentReadyState: document.readyState
		});
		// Boot succeeded: the app.html chunk-failure reload guard must not
		// outlive init, or a deploy could reload a live page.
		window.__iliadBootGuardDisarm?.();
		if (!isAuthAction) void initializeAnalyticsConsent();
		// Ensure diagnostic identity is maintained on every Aqueduct surface,
		// including public pages which do not otherwise need authentication.
		if (IS_AQUEDUCT && !isAuthAction) void import('$lib/authStore');
		// The favicon link itself lives in app.html so it is present from the
		// first byte; overrides mutate its href (the only swap Chrome honors).
		const icon: HTMLLinkElement | null = document.querySelector('link[rel="icon"]');
		const defaultFavicon: string = IS_AQUEDUCT
			? '/favicon-aqueduct-droplet.png'
			: '/brand/iliad-mark.svg';
		return faviconOverride.subscribe((url: string | null) => {
			icon?.setAttribute('href', url ?? defaultFavicon);
		});
	});
</script>

<svelte:head>
	<link rel="preconnect" href={`https://${FIREBASE_AUTH_DOMAIN}`} />
	<link rel="canonical" href={canonicalUrl} />
	{#if IS_AQUEDUCT && publicIndexPage($page.url.pathname)}
		<link
			rel="alternate"
			hreflang="en"
			href={new URL(localizedPath($page.url.pathname, 'en'), SITE_ORIGIN).href}
		/>
		<link
			rel="alternate"
			hreflang="ja"
			href={new URL(localizedPath($page.url.pathname, 'ja'), SITE_ORIGIN).href}
		/>
		<link
			rel="alternate"
			hreflang="x-default"
			href={new URL(localizedPath($page.url.pathname, 'en'), SITE_ORIGIN).href}
		/>
	{/if}
	{#if !IS_AQUEDUCT}
		<meta name="google-site-verification" content="r8LmzxO4Wu7Tj8h2tsPeR7bCQ8dcrocRDRqwfmOMUlU" />
	{/if}
</svelte:head>

{@render children()}
{#if !isAuthAction}
	<AnalyticsConsentBanner />
{/if}
